import { useEffect, useState, useRef } from 'react';
import { CalendarCheck, AlertTriangle, Loader2 } from 'lucide-react';
import api from '@host/axios';
// Luật số ngày đơn xin + đủ phép — SINH từ kernel (leaveBalance.js), cùng một bản với chốt gửi đơn
// và số ngày trừ lúc duyệt ở cả ba host. Đừng tự đếm ngày ở đây.
import { leaveRequestDays, checkLeaveBalance, calcRemaining, fmtDays } from '../../utils/leaveBalancePolicy';

import { Trans } from '@lingui/react/macro';

// Marker đẩy lên form_data khi không đủ phép → picker disable nút gửi + chặn submit.
export const LEAVE_INSUFFICIENT_MARKER = '__LEAVE_INSUFFICIENT__';

/**
 * LeaveBalanceCard — thành phần CHUYÊN cho kind 'leave' (như AttendanceLookupCard cho
 * chấm công). Read-only, KHÔNG submit giá trị.
 *
 * Đọc loại nghỉ (leave_type) + cả form (khoảng nghỉ + ô «Thời gian nghỉ» nếu mẫu có) → số ngày
 * đơn xin theo kernel `leaveRequestDays` (Buổi sáng/chiều = 0,5 · 1/4 ngày = 0,25) → nếu chọn
 * "paid" (nghỉ có lương = trừ phép) mà số ngày > phép còn lại → cảnh báo đỏ + khoá nút gửi.
 * Trước 28/09/2026 thẻ tự đếm theo LỊCH (30/09→30/09 = 1 ngày) ⇒ còn 0,5 ngày mà xin nghỉ buổi
 * chiều bị chặn, trong khi lúc duyệt máy chủ chỉ trừ 0,5.
 */
export default function LeaveBalanceCard({ leaveType, range, formFields, formData, readOnly, onChange }) {
  const [bal, setBal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const lastMarkerRef = useRef(undefined);

  useEffect(() => {
    if (readOnly) { setLoading(false); return; }
    let alive = true;
    const year = new Date().getFullYear();
    api.get('/leave-balance/me', { params: { year } })
      .then(r => { if (alive) setBal(r.data?.data ?? r.data ?? null); })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [readOnly]);

  // ── Tính (null-safe để hooks đứng trước early-return) ──
  const remaining = bal
    ? (bal.remaining_days != null ? Number(bal.remaining_days)
      : bal.remaining != null ? Number(bal.remaining)
      : calcRemaining(bal))
    : 0;
  const requested = leaveRequestDays({ formFields, formData, range });
  const hasBal = !readOnly && !failed && !!bal;
  const chk = checkLeaveBalance({ remaining, requestedDays: requested, leaveType });
  const shortfall = hasBal ? chk.shortfall : 0;
  const blocked = shortfall > 0;

  // Đẩy marker chặn lên form (picker disable nút + chặn submit). Ref guard → không loop.
  useEffect(() => {
    if (readOnly || !onChange) return;
    const marker = blocked ? LEAVE_INSUFFICIENT_MARKER : '';
    if (lastMarkerRef.current === marker) return;
    lastMarkerRef.current = marker;
    onChange(marker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, readOnly]);

  // Chế độ xem (người duyệt / đơn đã gửi): ẩn — /leave-balance/me là phép của NGƯỜI XEM.
  if (readOnly) return null;
  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-2 text-xs text-gray-400">
        <Loader2 size={13} className="animate-spin" /> <Trans>Đang tải số phép...</Trans>
      </div>
    );
  }
  // Tenant chưa wire số phép → ẩn, không chặn.
  if (failed || !bal) return null;

  return (
    <div className={`rounded-xl border p-3 ${blocked ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center gap-2 text-sm flex-wrap">
        <CalendarCheck size={15} className={blocked ? 'text-red-500' : 'text-gray-400'} />
        <span className="text-gray-500"><Trans>Phép còn lại:</Trans></span>
        <span className={`font-bold ${remaining > 0 ? 'text-gray-900' : 'text-red-600'}`}>{fmtDays(remaining)} <Trans>ngày</Trans></span>
        {requested > 0 && <span className="text-gray-400"><Trans>· đơn xin</Trans> {fmtDays(requested)} <Trans>ngày</Trans></span>}
      </div>
      {blocked && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-red-700">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span><b><Trans>Không đủ phép — không gửi được đơn nghỉ có lương.</Trans></b> <Trans>Còn</Trans> {fmtDays(remaining)} <Trans>ngày, đơn xin</Trans> {fmtDays(requested)} <Trans>ngày (thiếu</Trans> {fmtDays(shortfall)} <Trans>ngày). Chọn "Nghỉ không lương" hoặc giảm số ngày.</Trans></span>
        </div>
      )}
    </div>
  );
}
